import { DropdownMenu } from "@features/atoms/DropdownMenu"
import { MORE_ICON } from "@features/atoms/svg"
import { Spinner } from "@features/motion/Spinner"
import { useEventManifest } from "@hooks/useEventManifest"
import { cn } from "@utils/tw"
import dayjs from "dayjs"
import type { Spektrix } from "../../server/lib/spektrix-types"
import { useCheckout } from "./useCheckout"

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

export function CartPanel({ onTicketsRemoved }: { onTicketsRemoved?: () => void } = {}) {
  const checkout = useCheckout()
  const basket = checkout.basket
  const sessions = groupTickets(basket?.tickets ?? [], (ticket) => ticket.instance.id)
  const memberships = basket?.membershipSubscriptions ?? []
  const bookingFee = basket?.transactionCommission?.waived ? 0 : (basket?.transactionCommission?.amount ?? 0)
  const total = basket?.total ?? 0
  const isEmpty = !sessions.length && !memberships.length

  return (
    <div className="flex h-full min-h-0 flex-col text-blue" aria-label="Order summary" aria-busy={checkout.isPending}>
      <div
        className={cn("min-h-0 flex-1 overflow-auto", isEmpty ? "flex items-center justify-center text-center" : "pb-5")}
      >
        {!basket && checkout.isPending ? (
          <p className="type-body-xs flex items-center gap-2" role="status">
            <Spinner aria-hidden="true" /> Loading your cart…
          </p>
        ) : !isEmpty ? (
          <div className="flex flex-col gap-7">
            {sessions.map((tickets) => (
              <CartSession key={tickets[0].instance.id} tickets={tickets} onTicketsRemoved={onTicketsRemoved} />
            ))}
            {memberships.map((subscription) => (
              <CartMembership key={subscription.id} subscription={subscription} />
            ))}
          </div>
        ) : (
          <p className="type-body-m" role="status">
            Your cart is empty.
          </p>
        )}
      </div>
      {!isEmpty && (
        <div className="shrink-0">
          <dl className="flex flex-col gap-2 border-t border-blue py-4">
            <CartTotalRow label="Subtotal" amount={total - bookingFee} />
            <CartTotalRow label="Booking fee" amount={bookingFee} />
          </dl>
          <dl className="border-t border-blue py-5">
            <CartTotalRow label="Total" amount={total} total />
          </dl>
        </div>
      )}
    </div>
  )
}

function CartMembership({ subscription }: { subscription: Spektrix.MembershipSubscription }) {
  const checkout = useCheckout()
  const title = subscription.membership.name

  return (
    <section>
      <div className="flex items-start justify-between gap-3">
        <h3 className="type-body-s min-w-0">{title}</h3>
        <DropdownMenu
          align="end"
          contentClassName="z-[62]"
          trigger={
            <button
              type="button"
              aria-label={`Options for ${title}`}
              disabled={checkout.isPending}
              className="flex size-[24px] shrink-0 items-center justify-center text-blue-light hover:text-blue disabled:opacity-50 outline-none"
            >
              {MORE_ICON}
            </button>
          }
          items={[
            {
              type: "item",
              label: "Remove",
              disabled: checkout.isPending,
              className: "type-body-xs text-blue data-[highlighted]:bg-white-tint",
              onSelect: () => void checkout.removeMembership(subscription.id),
            },
          ]}
        />
      </div>
      <dl className="mt-3">
        <CartTotalRow label="Membership" amount={subscription.total} />
      </dl>
    </section>
  )
}

function CartSession({ tickets, onTicketsRemoved }: { tickets: Spektrix.Ticket[]; onTicketsRemoved?: () => void }) {
  const checkout = useCheckout()
  const manifest = useEventManifest()
  const { event, instance } = tickets[0]
  const film = manifest.data?.events?.find((film) =>
    film.filmData?.spektrixEventIds?.includes(Number.parseInt(event.id, 10)),
  )
  const title = film ? film.title : event.name
  const prices = groupTickets(tickets, (ticket) => ticket.type.id)

  return (
    <section>
      <div className="flex items-start justify-between gap-3">
        <h3 className="type-body-s min-w-0">{title}</h3>
        <DropdownMenu
          align="end"
          contentClassName="z-[62]"
          trigger={
            <button
              type="button"
              aria-label={`Options for ${title}, ${dayjs(instance.start).format("ddd D MMM, h:mma")}`}
              disabled={checkout.isPending}
              className="flex size-[24px] shrink-0 items-center justify-center text-blue-light hover:text-blue disabled:opacity-50 outline-none"
            >
              {MORE_ICON}
            </button>
          }
          items={[
            {
              type: "item",
              label: "Remove",
              disabled: checkout.isPending,
              className: "type-body-xs text-blue data-[highlighted]:bg-white-tint",
              onSelect: async () => {
                await checkout.removeTickets(tickets.map((ticket) => ticket.id))
                onTicketsRemoved?.()
              },
            },
          ]}
        />
      </div>
      <p className="type-label-s mt-1 text-blue-light">
        {/* Spektrix supplies start in the venue's local time. */}
        {dayjs(instance.start).format("ddd D MMM, h:mma")}
      </p>
      <dl className="mt-3 flex flex-col gap-2">
        {prices.map((group) => (
          <CartTotalRow
            key={group[0].type.id}
            label={`${group[0].type.name} x ${group.length}`}
            amount={group.reduce((sum, ticket) => sum + ticket.total, 0)}
          />
        ))}
      </dl>
    </section>
  )
}

function CartTotalRow({ label, amount, total }: { label: string; amount: number; total?: boolean }) {
  return (
    <div className={cn("flex items-baseline justify-between gap-3", total ? "type-body-l" : "type-body-xs")}>
      <dt>{label}</dt>
      <dd className="shrink-0 tabular-nums">{currency.format(amount)}</dd>
    </div>
  )
}

function groupTickets(tickets: Spektrix.Ticket[], key: (ticket: Spektrix.Ticket) => string) {
  const groups = new Map<string, Spektrix.Ticket[]>()
  for (const ticket of tickets) {
    const id = key(ticket)
    const group = groups.get(id)
    if (group) group.push(ticket)
    else groups.set(id, [ticket])
  }
  return [...groups.values()]
}
